import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabase-client.ts';
// Import removed - now using dynamic loading from @books/ content
import Header from '../../components/Header/index.js';
import BackButton from '../../components/BackButton/BackButton.js';
import HintButtons from './HintButtons.js';
import { calculateFragmentRange } from '../../utils/fragmentRangeCalculator.js';
import { getBookByRoute, getBookChapters } from '../../config/bookRegistry.js';
import { logEngagementEvent } from '../../services/engagementTrackingService.js';
import './AssessmentPage.css';

// Toggle to show/hide CEFR level in subtitle (set to false to hide)
const SHOW_CEFR_LEVEL = false;

const AssessmentPage = ({ onLogout }) => {
  const { level, chunk } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Extract book name from current URL path
  const currentPath = window.location.pathname;
  const bookMatch = currentPath.match(/^\/([^/]+)/);
  const bookName = bookMatch ? bookMatch[1] : 'Roland';
  
  
  // Map URL book names to folder names
  const bookFolderMap = {
    'Roland': 'SongOfRoland',
    'DivineComedy': 'DivineComedy',
    'Odyssey': 'Odyssey',
    'Iliad': 'Iliad',
    'Antigone': 'Antigone',
    'Oedipus': 'Oedipus',
    'HuckFinn': 'HuckFinn'
  };
  
  const bookFolder = bookFolderMap[bookName] || bookName;
  const [userInfo, setUserInfo] = useState({ email: '', cefr_level: '', auth_id: '' });
  const [assessmentData, setAssessmentData] = useState(null);
  const [stanzas, setStanzas] = useState([]);
  const [fragmentRange, setFragmentRange] = useState({ start: 1, end: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAssessmentFooterOpen, setIsAssessmentFooterOpen] = useState(true);

  const bookConfig = getBookByRoute(bookName);
  const bookChapters = bookConfig ? getBookChapters(bookConfig.id) : [];
  const chapterMetadata = bookChapters.find(ch => ch.chunk === chunk);
  const hintsEnabled = bookConfig?.features?.hints !== false;

  // Helper function to generate subtitle based on book type
  const generateSubtitle = (bookName, level, chunk, stanzas, fragmentRange) => {
    // Song of Roland and HuckFinn use fragment range | CEFR Level format
    if (bookName === 'Roland' || bookName === 'HuckFinn') {
      const fragmentCount = stanzas.length || (chunk ? parseInt(chunk.replace('chunk', '')) + 1 : 1);
      return SHOW_CEFR_LEVEL ? `Fragments 1-${fragmentCount} | CEFR Level: ${level}` : `Fragments 1-${fragmentCount}`;
    }
    
    // For epic and dramatic works, use cumulative fragments format
    if (bookName === 'Odyssey' || bookName === 'Antigone') {
      return SHOW_CEFR_LEVEL ? `Fragments ${fragmentRange.start}-${fragmentRange.end} | CEFR Level: ${level}` : `Fragments ${fragmentRange.start}-${fragmentRange.end}`;
    }
    
    // Default fallback
    return SHOW_CEFR_LEVEL ? `Assessment | CEFR Level: ${level}` : `Assessment`;
  };

  // Fetch user info and assessment data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch user info
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.email) {
          const { data } = await supabase
            .from('USERS')
            .select('email, cefr_level, auth_id, class_id')
            .eq('email', user.email)
            .single();
          if (data) {
            setUserInfo({ email: data.email, cefr_level: data.cefr_level, auth_id: data.auth_id, class_id: data.class_id });
          }
        }

        // Load content data to get fragment count for subtitle
        try {
          let contentData = null;
          
          if (bookName === 'Iliad' || bookName === 'Odyssey') {
            // For Iliad and Odyssey, load all fragments for the current book
            const currentBookNumber = parseInt(chunk.replace('chunk', '')) + 1;
            const bookNumberPadded = currentBookNumber.toString().padStart(3, '0');
            
            const allFragments = [];
            let fragmentIndex = 0;
            let hasMoreFragments = true;
            
            while (hasMoreFragments) {
              const fragmentNumber = fragmentIndex.toString().padStart(3, '0');
              const fileName = bookName === 'Iliad' 
                ? `iliad#${bookNumberPadded},${fragmentNumber}_last.json`
                : `odyssey#${bookNumberPadded},${fragmentNumber}_last.json`;
              const encodedFileName = encodeURIComponent(fileName);
              const filePath = `/content/books/${bookFolder}/adaptedContent/${level}/${encodedFileName}`;
              
              try {
                const response = await fetch(filePath);
                
                if (response.ok) {
                  const data = await response.json();
                  if (data.fragments && data.fragments.length > 0) {
                    allFragments.push(...data.fragments);
                    fragmentIndex++;
                  } else {
                    hasMoreFragments = false;
                  }
                } else {
                  hasMoreFragments = false;
                }
              } catch (error) {
                hasMoreFragments = false;
              }
            }
            
            contentData = { fragments: allFragments };
          } else {
            // For other books (Oedipus, Antigone, etc.), use the single chunk file
            const contentPath = `/content/books/${bookFolder}/adaptedContent/${level}/${chunk}.json`;
            const contentResponse = await fetch(contentPath);
            
            if (contentResponse.ok) {
              contentData = await contentResponse.json();
            }
          }
          
          if (contentData) {
            // Extract stanzas/fragments for fragment count
            if (contentData.stanzas && contentData.stanzas.length > 0) {
              setStanzas(contentData.stanzas);
            } else if (contentData.fragments && contentData.fragments.length > 0) {
              // For dramatic works that use fragments instead of stanzas
              setStanzas(contentData.fragments);
            }
          }
        } catch (error) {
        }

        // Calculate fragment range for all books
        const range = await calculateFragmentRange(bookName, level, chunk, bookFolder);
        setFragmentRange(range);

        // Load assessment data dynamically from @books/ content
        try {
          const chunkName = chunk?.replace('chunk', '') || '0';
          const assessmentPathWithLevel = `/content/books/${bookFolder}/assessments/${level}/${chunk}.json`;
          const assessmentPath = `/content/books/${bookFolder}/assessments/${chunk}.json`;

          const fetchAssessmentJson = async (path) => {
            const response = await fetch(path);
            if (!response.ok) {
              return null;
            }
            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
              return null;
            }
            return response.json();
          };

          let assessmentData = await fetchAssessmentJson(assessmentPathWithLevel);

          if (!assessmentData) {
            assessmentData = await fetchAssessmentJson(assessmentPath);
          }

          if (assessmentData) {
            const chapterNumber = parseInt(chunkName, 10) + 1;

            const isAvailable = assessmentData.available !== false &&
              assessmentData.status !== 'coming_soon' &&
              !assessmentData.placeholder &&
              (assessmentData.formUrl || assessmentData.link);

            setAssessmentData({
              available: isAvailable,
              title: assessmentData.title || `${bookName} - Book ${chapterNumber} Assessment`,
              description: assessmentData.description || `Assessment for ${bookName} Book ${chapterNumber}`,
              formUrl: assessmentData.formUrl || assessmentData.link,
              instructions: assessmentData.instructions || ['Complete this assessment to test your understanding.'],
              chapterNumber: chapterNumber,
              placeholder: assessmentData.placeholder || false,
              status: assessmentData.status || 'unknown',
              message: assessmentData.message || 'Assessment is not available at this time.',
              hints: assessmentData.hints || []
            });
          } else {
            setAssessmentData({ available: false });
          }
        } catch (error) {
          setAssessmentData({ available: false });
        }
      } catch (error) {
        setError('Failed to load assessment data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [level, chunk, bookName, bookFolder]);

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const handleReferenceContent = () => {
    // Navigate back to the specific reading page that the user came from
    const chapterNumber = searchParams.get('chapter') || '1';
    const bookRoute = `/${bookName}`;
    navigate(`${bookRoute}/read/${level}/${chunk}?chapter=${chapterNumber}`);
  };

  const handleExit = () => {
    const currentUserId = userInfo.auth_id || localStorage.getItem('auth_id') || 'anonymous';
    const currentUserLevel = userInfo.cefr_level || localStorage.getItem('cefr_level') || 'unknown';
    const resolvedBookId = bookConfig?.bookId || bookFolder;
    const resolvedBookTitle = bookConfig?.displayName || bookName;
    const assessmentPath = `/${bookName}/${level}/assessment/${chunk}`;
    const eventProperties = {
      path: assessmentPath,
      book_id: resolvedBookId,
      book_title: resolvedBookTitle,
      user_id: currentUserId,
      cefr_level: currentUserLevel,
    };

    void logEngagementEvent('exit_assessment_button', eventProperties);

    // Track assessment exit with Mixpanel
    if (window.mixpanel) {
      window.mixpanel.track('exit_assessment_button', eventProperties);
      window.mixpanel.start_batch_senders();
    }
    
    // Navigate back to chapter list
    const bookRoute = `/${bookName}`;
    navigate(bookRoute);
  };

  if (loading) {
    return (
      <div className="assessment-page">
        <Header userInfo={userInfo} onLogout={handleLogout} compact={true} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assessment-page">
        <Header userInfo={userInfo} onLogout={handleLogout} compact={true} />
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={handleExit} className="exit-btn">Back to Chapters</button>
        </div>
      </div>
    );
  }

  // No assessment available
  if (!assessmentData || !assessmentData.available) {
    const isPlaceholder = assessmentData?.placeholder || assessmentData?.status === 'coming_soon';
    const title = isPlaceholder ? 'Assessment Coming Soon' : 'No Assessment Available';
    const message = isPlaceholder 
      ? (assessmentData?.message || 'This assessment is currently being prepared and will be available soon.')
      : `There is no assessment available for this chapter.`;
    
    return (
      <div className="assessment-page">
        <Header userInfo={userInfo} onLogout={handleLogout} compact={true} />
        <div className="no-assessment-container">
          <div className="no-assessment-content">
            <h2>{title}</h2>
            <p>{message}</p>
            {isPlaceholder && assessmentData?.topics && (
              <div className="coming-soon-info">
                <h3>Assessment Topics (Preview):</h3>
                <ul>
                  {assessmentData.topics.map((topic, index) => (
                    <li key={index}>{topic}</li>
                  ))}
                </ul>
                <p><strong>Estimated Time:</strong> {assessmentData.estimatedTime || 'TBD'}</p>
              </div>
            )}
            <button onClick={handleReferenceContent} className="exit-btn">Back to Content</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-page">
      {/* Header Banner */}
      <Header userInfo={userInfo} onLogout={handleLogout} />
      
      {/* Back Button */}
      <BackButton 
        onClick={handleReferenceContent}
        text="Back to Reading"
      />
      
      {/* Assessment Content */}
      <div className="assessment-container">
        <h1 className="assessment-title">
          {`Chapter ${chapterMetadata?.chapt_num || parseInt(chunk.replace('chunk', ''), 10) + 1} Assessment`}
        </h1>
        
        <p className="assessment-subtitle">
          {chapterMetadata?.subtitle || generateSubtitle(bookName, level, chunk, stanzas, fragmentRange)}
        </p>
        
         {/* Assessment form with an optional hint panel */}
         <div className={`assessment-content-layout ${hintsEnabled ? '' : 'without-hints'}`}>
          {/* Left side: Google Form iframe */}
          <div className="form-container">
            <iframe
              src={assessmentData.formUrl}
              width="100%"
              height="800"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="Assessment Form"
              className="assessment-iframe"
            >
              Loading…
            </iframe>
          </div>
          
          {hintsEnabled && (
            <HintButtons
              bookName={bookName}
              level={level}
              chunk={chunk}
              hints={assessmentData.hints || []}
              onHintClick={(hintNumber) => {
                // Track hint button click with Mixpanel
                if (window.mixpanel) {
                  window.mixpanel.track('hint_button_pressed', {
                    hintNumber: hintNumber
                  });
                }
              }}
              onReferenceContent={handleReferenceContent}
              onExit={handleExit}
            />
          )}
         </div>
         <div className={`assessment-sticky-footer ${isAssessmentFooterOpen ? '' : 'assessment-sticky-footer-collapsed'}`}>
           <button
             className="assessment-footer-toggle"
             type="button"
             onClick={() => setIsAssessmentFooterOpen(prevOpen => !prevOpen)}
             aria-expanded={isAssessmentFooterOpen}
             aria-label={isAssessmentFooterOpen ? 'Hide assessment footer' : 'Show assessment footer'}
             title={isAssessmentFooterOpen ? 'Hide assessment footer' : 'Show assessment footer'}
           >
             <i className={`fa-solid fa-circle-chevron-${isAssessmentFooterOpen ? 'down' : 'up'}`} />
           </button>
           <div className="assessment-footer-content">
             <p className="assessment-completion-text">
               After submitting the Google Form, click the button below to exit the assessment.
             </p>
             <button
               className="assessment-exit-btn"
               onClick={handleExit}
             >
               Submit my Assessment
             </button>
           </div>
         </div>
      </div>


    </div>
  );
};

export default AssessmentPage;
